function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Col, Container, Row } from "react-bootstrap";
import { Modal, Button } from "@dpdgroupuk/mydpd-ui";
import ResultsPanel from "../ResultsPanel";
import Form from "../Form";
import "./styles.scss";

const AddressLookupModal = ({
  isOpen,
  onClose,
  onSelect,
  search,
  onClear,
  onFind,
  onFocus,
  classes,
  onBlur,
  ...modalProps
}) => {
  const [addresses, setAddresses] = React.useState([]);
  const [error, setError] = React.useState(null);
  const promiseRef = React.useRef();
  useEffect(() => {
    if (error) {
      setError(null);
    }
  }, [isOpen]);
  const abortIfPending = React.useCallback(() => {
    if (promiseRef.current && typeof promiseRef.current.abort === "function") {
      promiseRef.current.abort();
    }
  }, [promiseRef.current]);
  const onHide = React.useCallback(() => {
    onClose();
    abortIfPending();
    setError(null);
    setAddresses([]);
  }, [onClose, abortIfPending, setError, setAddresses]);
  const onSelectAddress = React.useCallback((e, address) => {
    onSelect(address);
    onHide();
  }, [onSelect, onHide]);
  const searchWithStatusHandler = React.useCallback(params => {
    const searching = search(params);
    searching.finally(() => {
      promiseRef.current = null;
    });
    promiseRef.current = searching;
    return searching;
  }, [abortIfPending]);
  const onSubmit = React.useCallback(async ({
    postcode,
    ...values
  }) => {
    onFind();
    setAddresses([]);
    setError(null);

    try {
      const postcodeWithoutSpaces = postcode ? postcode.replaceAll(" ", "") : postcode;
      const addresses = await searchWithStatusHandler({ ...values,
        postcode: postcodeWithoutSpaces
      });

      if (!addresses || !addresses.length) {
        throw new Error("Not found");
      }

      setAddresses(addresses && addresses.length ? addresses : []);
    } catch (e) {
      setError(e.message || e);
    }
  }, [searchWithStatusHandler, setError, setAddresses, onFind]);
  const onClearClick = React.useCallback(async () => {
    onClear();
    setAddresses([]);
    setError(null);
  }, [onClear]);
  return /*#__PURE__*/React.createElement(Modal, _extends({
    id: "postcode-finder-modal",
    dialogClassName: classes && classes.container,
    show: isOpen
  }, modalProps), /*#__PURE__*/React.createElement(Modal.Header, {
    className: `postcode-finder-header ${classes && classes.header}`,
    closeButton: false
  }, "Address Lookup"), /*#__PURE__*/React.createElement(Modal.Body, {
    className: `postcode-finder-content ${classes && classes.body}`
  }, /*#__PURE__*/React.createElement(Container, {
    fluid: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("div", {
    className: "left-content"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit,
    onClear: onClearClick,
    onFocus: onFocus,
    onBlur: onBlur
  }))), /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("div", {
    className: "right-content"
  }, /*#__PURE__*/React.createElement(ResultsPanel, {
    addresses: addresses,
    error: error,
    onSelect: onSelectAddress
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "dark",
    onClick: onHide,
    className: "postcode-finder-cancel"
  }, "Cancel")))))));
};

AddressLookupModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  search: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onFind: PropTypes.func,
  onFocus: PropTypes.func,
  classes: PropTypes.object,
  ...Modal.propTypes
};
AddressLookupModal.defaultProps = {
  onClose: () => null,
  onClear: () => null,
  onFind: () => null,
  onFocus: () => null,
  onHide: () => null
};
export default AddressLookupModal;